import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { consumeRedirect } from '../utils/postLoginRedirect';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);

  // Helper function to check if token is expired or will expire soon
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      // Consider token expired if it expires within 5 minutes (increased from 2 minutes for better reliability during long breaks)
      return payload.exp < (currentTime + 300);
    } catch (error) {
      return true;
    }
  };

  // Helper function to refresh token with retry logic
  const refreshToken = async (retryCount = 0) => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      logout();
      return null;
    }

    const maxRetries = 3;
    const retryDelays = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      return data.token;
    } catch (error) {
      console.error(`Token refresh error (attempt ${retryCount + 1}/${maxRetries}):`, error);
      
      // Retry with exponential backoff if we haven't exceeded max retries
      if (retryCount < maxRetries - 1) {
        const delay = retryDelays[retryCount];
        console.log(`Retrying token refresh in ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return refreshToken(retryCount + 1);
      }
      
      // All retries failed, logout user
      console.error('Token refresh failed after all retries');
      logout();
      return null;
    }
  };

  // Helper function to schedule token refresh
  const scheduleTokenRefresh = (token) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      // Refresh token 5 minutes before expiry (increased from 2 minutes for better reliability during long breaks)
      const refreshTime = Math.max(0, (timeUntilExpiry - 300) * 1000);
      
      refreshTimeoutRef.current = setTimeout(async () => {
        const newToken = await refreshToken();
        if (newToken) {
          scheduleTokenRefresh(newToken);
        }
      }, refreshTime);
    } catch (error) {
      console.error('Error scheduling token refresh:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserData(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Identify the logged-in user to the activity tracker. The tracker script
  // is loaded async in index.html, so it may not be ready when the user is
  // first set — poll briefly until window.ActivityTracker is available.
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    const tryIdentify = () => {
      if (cancelled) return false;
      if (window.ActivityTracker && typeof window.ActivityTracker.identify === 'function') {
        try {
          window.ActivityTracker.identify(user.email);
        } catch (e) {
          console.error('ActivityTracker identify failed:', e);
        }
        return true;
      }
      return false;
    };
    if (tryIdentify()) return;
    const interval = setInterval(() => {
      if (tryIdentify()) clearInterval(interval);
    }, 500);
    // Stop polling after 30s so we never spin forever if the tracker fails to load
    const timeout = setTimeout(() => clearInterval(interval), 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [user?.email]);

  const fetchUserData = async (token) => {
    try {
      // Check if token is expired and refresh if needed
      if (isTokenExpired(token)) {
        const newToken = await refreshToken();
        if (!newToken) {
          setUser(null);
          setLoading(false);
          return;
        }
        token = newToken;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Schedule automatic token refresh
        scheduleTokenRefresh(token);
      } else if (response.status === 401) {
        // Try to refresh token once more
        const newToken = await refreshToken();
        if (newToken) {
          // Retry the request with new token
          const retryResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${newToken}`,
            },
          });
          if (retryResponse.ok) {
            const userData = await retryResponse.json();
            setUser(userData);
            scheduleTokenRefresh(newToken);
          } else {
            logout();
          }
        } else {
          logout();
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      setUser(data.user);
      // Schedule automatic token refresh
      scheduleTokenRefresh(data.token);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginMicrosoft = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    // Send our origin to the backend so the resolveFrontendUrl resolver (routes/auth.js,
    // commit 05cc51a) can redirect us back to the same host we came from, instead of the
    // hardcoded FRONTEND_URL fallback. The whole callback URL — including this ?origin=
    // — is URL-encoded when handed to Bot@Work, so it survives the round-trip.
    // Backend must have this origin in ALLOWED_FRONTEND_URLS (default allowlist already
    // includes http://localhost:3000 and https://pm.onfulcrum.com).
    const callbackUrl = `${apiUrl}/auth/microsoft/callback?origin=${encodeURIComponent(window.location.origin)}`;
    const appId = "Beta@Work";
    const loginUrl = `https://api.botatwork.com/azure/login?app_id=${appId}&callback_url=${encodeURIComponent(callbackUrl)}`;

    console.log('🔍 Microsoft Auth Debug Info:');
    console.log('   API URL:', apiUrl);
    console.log('   Origin hint:', window.location.origin);
    console.log('   Callback URL:', callbackUrl);
    console.log('   Login URL:', loginUrl);

    // Full-page redirect path (no popup). This avoids Cross-Origin-Opener-Policy
    // problems that silently drop the popup → parent postMessage / storage
    // event. The backend redirects to /auth-callback, which already handles
    // the no-opener case by writing tokens to localStorage and navigating
    // straight to /dashboard.
    window.location.href = loginUrl;
    return;
    // ----- Legacy popup path kept below for reference, never executed -----
    // eslint-disable-next-line no-unreachable
    const popup = window.open(loginUrl, '_blank', 'width=500,height=600');
    
    if (!popup) {
      console.error('❌ Popup blocked by browser');
      alert('Popup blocked! Please allow popups for this site and try again.');
      return;
    }
    
    console.log('✅ Popup opened successfully');

    // -------- COOP-resilient fallback: storage event listener --------
    // Modern browsers' Cross-Origin-Opener-Policy can sever window.opener
    // and silently drop the popup → parent postMessage. The popup already
    // writes the auth token to localStorage in AuthCallback.js, so we can
    // detect a successful login by listening for the 'storage' event in
    // the parent window. This event fires in OTHER windows when localStorage
    // is modified — which is exactly what the popup does.
    const handleStorageAuth = async (newToken) => {
      try {
        const res = await fetch(`${apiUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        if (!res.ok) {
          console.error('❌ /auth/me failed after storage event:', res.status);
          return;
        }
        const userData = await res.json();
        console.log('🎉 User logged in successfully (via storage event)');
        setUser(userData);
        scheduleTokenRefresh(newToken);
        cleanupListeners();
        try { popup.close(); } catch (e) { /* COOP-blocked, ignore */ }
        const redirectPath = userData.role === 'partner' ? '/tasks' : '/dashboard';
        console.log(`🚀 Redirecting to ${redirectPath}...`);
        setTimeout(() => { window.location.href = redirectPath; }, 500);
      } catch (err) {
        console.error('Storage-triggered user fetch failed:', err);
      }
    };
    const storageListener = (event) => {
      if (event.key === 'token' && event.newValue && !event.oldValue) {
        console.log('🔁 Storage event: popup wrote auth token');
        handleStorageAuth(event.newValue);
      }
    };
    window.addEventListener('storage', storageListener);

    const cleanupListeners = () => {
      window.removeEventListener('storage', storageListener);
      window.removeEventListener('message', messageListener);
      clearInterval(checkClosed);
      clearTimeout(authTimeout);
    };
    // -------- end fallback wiring --------

    // Listen for messages from popup
    const messageListener = (event) => {
      console.log('📨 Received message:', event.data);
      console.log('   Origin:', event.origin);
      console.log('   Expected origin:', window.location.origin);
      
      // Accept messages from both frontend and backend origins
      const allowedOrigins = [
        window.location.origin, 
        apiUrl, 
        'http://localhost:5000', 
        'http://localhost:3000',
        'https://focalpointbackend-512429445794.us-central1.run.app',
        'https://focalpointbackend-512429445794.us-central1.run.app/api',
        'https://focalpoint.netlify.app',
        'https://focalpoint-app.netlify.app',
        'null' // Sometimes popup messages come with null origin
      ];
      
      // More lenient origin checking for production
      const isAllowedOrigin = allowedOrigins.includes(event.origin) || 
                             event.origin === 'null' || 
                             event.origin.includes('focalpointbackend') ||
                             event.origin.includes('netlify.app');
      
      if (!isAllowedOrigin) {
        console.log('❌ Message from unauthorized origin, ignoring');
        console.log('   Received from:', event.origin);
        console.log('   Allowed origins:', allowedOrigins);
        return;
      }
      
      if (event.data.type === 'MICROSOFT_AUTH_SUCCESS') {
        console.log('✅ Microsoft authentication successful');
        const { token, refreshToken, user } = event.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(user);
        scheduleTokenRefresh(token);
        
        // Close popup and clean up
        try {
          popup.close();
        } catch (error) {
          console.log('ℹ️  Could not close popup (security policy)');
        }
        window.removeEventListener('message', messageListener);
        window.removeEventListener('storage', storageListener);
        clearInterval(checkClosed);
        clearTimeout(authTimeout);

        console.log('🎉 User logged in successfully');

        // Back to where they were heading (e.g. an approval link), else by role.
        const redirectPath = consumeRedirect() || (user.role === 'partner' ? '/tasks' : '/dashboard');
        console.log(`🚀 Redirecting to ${redirectPath}...`);

        setTimeout(() => {
          window.location.href = redirectPath;
        }, 1000);
      } else if (event.data.type === 'MICROSOFT_AUTH_ERROR') {
        console.error('❌ Microsoft auth error:', event.data.error);
        alert(`Microsoft authentication failed: ${event.data.error}`);
        try {
          popup.close();
        } catch (error) {
          console.log('Could not close popup:', error);
        }
        window.removeEventListener('message', messageListener);
        clearInterval(checkClosed);
      }
    };
    
    window.addEventListener('message', messageListener);
    console.log('👂 Listening for popup messages...');
    
    // Set a timeout to handle cases where popup doesn't respond
    const authTimeout = setTimeout(() => {
      console.log('⏰ Authentication timeout - no response from popup');
      alert('Authentication timed out. Please try again.');
      try {
        popup.close();
      } catch (error) {
        console.log('Could not close popup:', error);
      }
      window.removeEventListener('message', messageListener);
      window.removeEventListener('storage', storageListener);
      clearInterval(checkClosed);
    }, 60000); // 60 second timeout
    
    // Check if popup is closed manually (with error handling for COOP policy)
    const checkClosed = setInterval(() => {
      try {
        if (popup.closed) {
          console.log('🔄 Popup closed manually');
          clearInterval(checkClosed);
          clearTimeout(authTimeout);
          window.removeEventListener('message', messageListener);
        }
      } catch (error) {
        // Cross-Origin-Opener-Policy blocks access to popup.closed
        // This is normal and expected - we'll rely on message events instead
        console.log('ℹ️  Cannot check popup status due to security policy (this is normal)');
        // Don't clear the interval immediately - let it run for a bit to catch messages
        // Clear it after 30 seconds to prevent memory leaks
        setTimeout(() => {
          clearInterval(checkClosed);
          clearTimeout(authTimeout);
          window.removeEventListener('message', messageListener);
        }, 30000);
      }
    }, 1000);
  };

  const logout = async () => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Call backend logout to invalidate refresh token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginMicrosoft,
    logout,
    refreshToken,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 